import React, { useCallback, useMemo } from "react";
import { MainLayout } from "../layout/mainLayout";
import { LoadingIndicator } from "../framework/loadingIndicatorNew";
import { VehiclePositionInfo } from "../../models/vehicle/vehiclePosition";
import { MonitorViewMap } from "./monitorViewMap";
import { MonitorViewSidebar } from "./sidebar/monitorViewSidebar";
import { Base } from "../../framework/base";
import { useAppSelector } from "../../framework/customStore";

import { EmployeeData, MonitorViewMainLayoutProps, VehicleData } from "../../models/monitorView/monitorViewTypes";
import { EmployeeListItemDto } from "../../models/employee/employee";
import { WorkShiftDto } from "../../models/workShift/workShift";
import { date } from "yup";

export const MonitorViewMainLayout = ({
    positions,
    loading,
    workShifts,
    reloadKey,
    onRefresh
}: MonitorViewMainLayoutProps) => {

    const employees = useAppSelector((state) => state.employees.employees);
    const showNoWorkTime = useAppSelector((state) => state.monitorViewFilters.noWorkTime);
    const noWork = useAppSelector((state) => state.monitorViewFilters.noWork);

    const selectedWorkTimeTypeIds = useAppSelector((state) => state.monitorViewFilters.workTimeTypeIds);

    const {
        selectedVehicleGroupIds,
        selectedVehicleStatusFilter } = useAppSelector((state) => state.monitorViewFilters);
    const vehicles = useAppSelector((state) => state.monitorView.vehicles);

    const {
        isVehicleRouteView,
        mapData
    } = useAppSelector((state) => state.monitorView);

    const filterByWorkTimeType = useCallback(
        (p: VehiclePositionInfo) =>
            !selectedWorkTimeTypeIds ||
            p.employeeWorkTimeTypes.some((w) => selectedWorkTimeTypeIds?.includes(w.workTimeTypeId)),
        [selectedWorkTimeTypeIds]
    );

    const applyFilters = useCallback(
        (p: VehiclePositionInfo) => {
            const hasNoWorkTime = p.employeeWorkTimeTypes.length === 0;
            const matchesWorkTimeType = filterByWorkTimeType(p);
            return (!showNoWorkTime && matchesWorkTimeType) || (showNoWorkTime && hasNoWorkTime) || matchesWorkTimeType;
        },
        [showNoWorkTime, filterByWorkTimeType]
    );

    const employeeFilter = useCallback(
        (p: VehiclePositionInfo) => {
            if (!p?.employeeWorkTimeTypes || p?.employeeWorkTimeTypes.length === 0) {
                return noWork;
            }
            const hasNoWorkTime = p.employeeWorkTimeTypes.length === 0 || p.employeeWorkTimeTypes[0].workTimeTypeId === null;
            const hasSelectedWorkTimeType = filterByWorkTimeType(p);
            return noWork ? hasNoWorkTime || hasSelectedWorkTimeType : hasSelectedWorkTimeType;
        },
        [noWork, filterByWorkTimeType]
    );

    const mapCommonData = (position: VehiclePositionInfo) => ({
        brand: position?.brand,
        carId: position?.carId,
        codriverGmt: position?.codriverGmt,
        codriverId: position?.codriverId,
        coords: { lat: position?.latitude, lng: position?.longitude },
        driverGmt: position?.driverGmt,
        driverId: position?.driverId,
        driving: position?.driving,
        drivingGmt: position?.drivingGmt,
        employeeWorkTimeTypes: position?.employeeWorkTimeTypes,
        latitude: position?.latitude,
        longitude: position?.longitude,
        registerNumber: position?.registerNumber,
        speed: position?.speed,
        timestamp: position?.timestamp,
        vehicleId: position?.vehicleId,
        workTimeTypeName: position?.employeeWorkTimeTypes[0]?.workTimeTypeName,
        workTimeTypeType: position?.employeeWorkTimeTypes[0]?.workTimeTypeType,
    });

    const mapVehicleData = (p: VehiclePositionInfo): VehicleData => ({
        ...mapCommonData(p),
        employeeName: p.employeeWorkTimeTypes[0]?.employeeName,
        employeeId: p.employeeWorkTimeTypes[0]?.employeeId,
    });

    const mapEmployeeData = (e: EmployeeListItemDto, positions: VehiclePositionInfo[], workShifts: WorkShiftDto[]): EmployeeData => {
        const position = positions?.find((p) => p.employeeWorkTimeTypes.some((w) => w.employeeId === e.id));
        return {
            ...mapCommonData(position),
            employeeId: e.id,
            employeeName: e?.fullName,
            salaryGroupId: e?.salaryGroupId,
            workShifts: workShifts.filter((w) => w.employeeId === e.id),
            currentWorkShift: workShifts
                .filter(w => w.employeeId === e.id && !w.endTime)
                .sort(
                    (a, b) =>
                        // If any change that two workshifts dont have end time set, select the one that started later
                        new Date(b.startTime).getTime() -
                        new Date(a.startTime).getTime()
                )?.[0]
        };
    };

    const vehicleData: VehicleData[] = useMemo(
        () =>
            positions
                ?.filter((p) => selectedVehicleGroupIds.length === 0 || selectedVehicleGroupIds.includes(vehicles.find((v) => v.id === p.vehicleId)?.vehicleGroupId)) // Filter by vehicle group
                .map(mapVehicleData)
                .sort((a, b) => Base.stringCompare(a.registerNumber, b.registerNumber))
                .sort((a, b) => Base.stringCompare(a.workTimeTypeName, b.workTimeTypeName)),
        [positions, selectedVehicleGroupIds]
    );

    const filteredVehicleData = useMemo(() => {
        if (selectedVehicleStatusFilter === "All") return vehicleData;
        return vehicleData.filter((vehicle) => vehicle.driving === selectedVehicleStatusFilter);
    }, [vehicleData, selectedVehicleStatusFilter]);

    const filteredEmployeeData: EmployeeData[] = useMemo(
        () =>
            employees
                ?.filter((e) => {
                    const position = positions?.find((p) => p.employeeWorkTimeTypes.some((w) => w.employeeId === e.id));
                    return employeeFilter(position);
                })
                .map((e) => mapEmployeeData(e, positions, workShifts))
                .filter((p) => selectedVehicleGroupIds.length === 0 || selectedVehicleGroupIds.includes(vehicles.find((v) => v.id === p.vehicleId)?.vehicleGroupId)) // Filter by vehicle group
                .sort((a, b) => Base.stringCompare(a.workTimeTypeName, b.workTimeTypeName)),
        [positions, employees, noWork, selectedWorkTimeTypeIds, selectedVehicleGroupIds, workShifts]
    );

    const activeEmployeesAndVehicles = useMemo(() => {
        let activeEmployees = 0;
        let activeVehicles = 0;
        positions
            ?.filter(applyFilters)
            ?.forEach(({ employeeWorkTimeTypes, driving }) => {
                if (employeeWorkTimeTypes?.length > 0) {
                    if (selectedVehicleStatusFilter === "All" || driving === selectedVehicleStatusFilter) {
                        activeVehicles++;
                    }
                    activeEmployees++;
                }
            });
        const totalEmployees = employees?.length || 0;
        const totalVehicles = positions?.length || 0;
        return {
            activeVehicles,
            totalVehicles,
            activeEmployees,
            totalEmployees,
        };
    }, [positions, applyFilters, selectedVehicleStatusFilter, employees]);

    const vehiclesAndEmployeesSidebarProps = useMemo(() => ({
        vehicleData: filteredVehicleData,
        employeeData: filteredEmployeeData
    }), [filteredVehicleData, filteredEmployeeData]);

    const mapDataMemo = useMemo(() => {
        return isVehicleRouteView ? mapData : filteredVehicleData;
    }, [filteredVehicleData, filteredEmployeeData, vehicleData, noWork, isVehicleRouteView, mapData]);

    return (
        <MainLayout
            noPadding
            sideComponentSide="left"
            sideComponent={
                <MonitorViewSidebar
                    fleetSummaryTotals={activeEmployeesAndVehicles}
                    vehiclesAndEmployeesSidebarProps={vehiclesAndEmployeesSidebarProps}
                />
            }
            sideOverFlowHidden
        >
            <>
                <LoadingIndicator loading={loading} />
                <MonitorViewMap
                    loading={loading}
                    data={mapDataMemo}
                    onRefresh={() => onRefresh(true)}
                    reloadKey={reloadKey}
                />
            </>
        </MainLayout>
    );
};